import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { errorCountsBySection, errorEntries, sectionIndexOf } from "utils/formErrors";

/** How long the ring stays on a revealed field, in ms. */
const FLASH_MS = 1200;
const FLASH_CLASS = "field-flash";
// Cancels a reveal in flight and takes the ring off whatever it lit.
const clearFlash = (h) => {
  window.cancelAnimationFrame(h.raf1);
  window.cancelAnimationFrame(h.raf2);
  window.clearTimeout(h.timer);
  if (h.el) h.el.classList.remove(FLASH_CLASS);
  h.el = null;
};

const FOCUSABLE = 'input:not([type="hidden"]), textarea, [role="combobox"], button, [tabindex]';

/**
 * What a form does when Save is refused: a summary banner of every error, a count on
 * each tab that holds one, and a jump to the first offending field.
 *
 * Why it exists: layout/Tabs mounts only the active panel, so a required field on
 * another tab failed validation where nobody could see it, and Save looked broken
 * (a client reported exactly that on /outlet_form). Every form wires it the same way:
 *
 *   const { onInvalid, reset, tabProps, summaryProps } =
 *     useValidationSummary({ errors, sections });
 *   if (validateForm()) { …save; reset() } else onInvalid();
 *   <FormErrorSummary {...summaryProps} />
 *   <Tabs {...tabProps} header={…}>…</Tabs>
 *
 * `sections` (see utils/formErrors) is index-aligned with the Tabs header; a form with
 * no tabs passes none and still gets the banner and the jump.
 *
 * The jump runs in an EFFECT on `errors`, not inside onInvalid: onInvalid is called in
 * the same tick as validateForm's setErrors, before the new errors have committed.
 * It then waits two animation frames — one for React to mount the tab it switched to,
 * one for layout — as useRevealControl does for the invoice-template inspector.
 */
export default function useValidationSummary({ errors, sections = [] } = {}) {
  const [tab, setTab] = useState(0);
  const [attempted, setAttempted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // A timestamp rather than a flag, so two failed saves in a row each jump.
  const [pendingAt, setPendingAt] = useState(0);
  const handles = useRef({ raf1: 0, raf2: 0, timer: 0, el: null });
  // The banner element. Fields are looked up inside the dialog (or form) holding it,
  // so a quick-add dialog never jumps to a same-named field on the page behind it.
  const bannerRef = useRef(null);

  const items = useMemo(() => errorEntries(errors, sections), [errors, sections]);
  const counts = useMemo(() => errorCountsBySection(errors, sections), [errors, sections]);

  useEffect(() => {
    const h = handles.current;
    return () => clearFlash(h);
  }, []);

  const reveal = useCallback(
    (field) => {
      const idx = sectionIndexOf(field, sections);
      if (idx > -1) setTab(idx);
      clearFlash(handles.current);

      const run = () => {
        const h = handles.current;
        const banner = bannerRef.current;
        const scope = banner?.closest('[role="dialog"], form') || document;
        let anchor =
          scope.querySelector(`[data-field="${CSS.escape(field)}"]`) ||
          scope.querySelector(`[name="${CSS.escape(field)}"]`);
        if (!anchor) {
          // Nothing to land on (a field rendered conditionally, or a key no control
          // shows) — at least bring the banner, which names it, into view.
          banner?.scrollIntoView({ behavior: "smooth", block: "nearest" });
          return;
        }
        // InputSwitch's root is `display: contents` and has no box of its own.
        if (anchor.getClientRects().length === 0 && anchor.firstElementChild)
          anchor = anchor.firstElementChild;

        anchor.scrollIntoView({ behavior: "smooth", block: "center" });
        const target = anchor.matches(FOCUSABLE) ? anchor : anchor.querySelector(FOCUSABLE);
        // preventScroll: the smooth scroll above is already on its way; a focus-scroll
        // would cut it short and leave the field at the edge of the screen.
        target?.focus({ preventScroll: true });

        anchor.classList.add(FLASH_CLASS);
        h.el = anchor;
        h.timer = window.setTimeout(() => clearFlash(h), FLASH_MS);
      };

      handles.current.raf1 = window.requestAnimationFrame(() => {
        handles.current.raf2 = window.requestAnimationFrame(run);
      });
    },
    [sections]
  );

  useEffect(() => {
    if (!pendingAt) return;
    setPendingAt(0);
    if (items.length > 0) reveal(items[0].field);
    // Only a new failed save should jump — not every keystroke that edits `errors`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAt, errors]);

  const onInvalid = useCallback(() => {
    setAttempted(true);
    setDismissed(false);
    setPendingAt(Date.now());
  }, []);

  const reset = useCallback(() => {
    setAttempted(false);
    setDismissed(false);
    clearFlash(handles.current);
  }, []);

  return {
    onInvalid,
    reset,
    reveal,
    tabProps: {
      value: tab,
      onTabChange: setTab,
      // Badges only once Save has been tried: before that, an error map left over from
      // an on-change check is not something the user asked to be told about.
      errorCounts: attempted ? counts : undefined,
    },
    summaryProps: {
      bannerRef,
      items,
      visible: attempted && !dismissed && items.length > 0,
      onSelect: reveal,
      onDismiss: () => setDismissed(true),
    },
  };
}
